import { Fragment, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigationType } from 'react-router-dom';
import {
  BookIcon,
  FlaskIcon,
  HistoryIcon,
  RssIcon,
  ShieldIcon,
  SlidersIcon,
  TimerIcon,
  VersusIcon,
  WalletIcon,
} from './icons';
import { getTrackedAddress } from '../lib/storage';
import { PAPER_TEST_PAGES } from '../lib/paperTestPages';
import styles from './Layout.module.css';

// Keep in sync with the tab bar breakpoint in Layout.module.css.
const DESKTOP_NAV_QUERY = '(min-width: 1001px)';

function MoreIcon({ open }: { open: boolean }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="22"
      height="22"
      aria-hidden="true"
      focusable="false"
      className={styles.navIcon}
    >
      {open ? (
        <path
          d="M6 6 L18 18 M18 6 L6 18"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
        />
      ) : (
        <path
          d="M5 12 H5.01 M12 12 H12.01 M19 12 H19.01"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
        />
      )}
    </svg>
  );
}

/**
 * Pages in topic order: the wallet, the crypto research, the sports
 * research, then reference pages. The desktop header separates the groups;
 * on phones the pages marked `tab` are tabs and the rest are listed by group
 * under More. The individual paper-test pages (PAPER_TEST_PAGES) are reached
 * from the Paper tests overview, which stays highlighted while one is open.
 */
const NAV_GROUPS = {
  wallet: 'Wallet',
  crypto: 'Crypto research',
  sports: 'Sports',
  about: 'About',
} as const;

type NavGroup = keyof typeof NAV_GROUPS;

interface NavItem {
  to: string;
  label: string;
  short: string;
  end: boolean;
  tab: boolean;
  group: NavGroup;
  Icon: typeof WalletIcon;
  /** Other paths that count as this page for highlighting. */
  covers?: readonly string[];
}

const NAV_ITEMS: NavItem[] = [
  {
    to: '/',
    label: 'Wallet',
    short: 'Wallet',
    end: true,
    tab: true,
    group: 'wallet',
    Icon: WalletIcon,
  },
  {
    to: '/history',
    label: 'History',
    short: 'History',
    end: false,
    tab: false,
    group: 'wallet',
    Icon: HistoryIcon,
  },
  {
    to: '/btc-15m-predictor',
    label: '15m Predictor',
    short: 'Predictor',
    end: false,
    tab: true,
    group: 'crypto',
    Icon: TimerIcon,
  },
  {
    to: '/paper-tests',
    label: 'Paper tests',
    short: 'Tests',
    end: false,
    tab: true,
    group: 'crypto',
    Icon: FlaskIcon,
    covers: PAPER_TEST_PAGES.map((page) => page.to),
  },
  {
    to: '/rug-check',
    label: 'Rug check',
    short: 'Rug check',
    end: false,
    tab: false,
    group: 'crypto',
    Icon: ShieldIcon,
  },
  {
    to: '/fight-market',
    label: 'Fight research',
    short: 'Fights',
    end: false,
    tab: true,
    group: 'sports',
    Icon: VersusIcon,
  },
  {
    to: '/methodology',
    label: 'Methodology',
    short: 'Methodology',
    end: false,
    tab: false,
    group: 'about',
    Icon: BookIcon,
  },
  {
    to: '/settings',
    label: 'Settings',
    short: 'Settings',
    end: false,
    tab: false,
    group: 'about',
    Icon: SlidersIcon,
  },
];

/** Consecutive runs of items that share a group, in NAV_ITEMS order. */
function byGroup(items: NavItem[]): Array<{ group: NavGroup; items: NavItem[] }> {
  const out: Array<{ group: NavGroup; items: NavItem[] }> = [];
  for (const item of items) {
    const last = out.at(-1);
    if (last?.group === item.group) last.items.push(item);
    else out.push({ group: item.group, items: [item] });
  }
  return out;
}

/**
 * RSS feeds, for pasting into a reader. They live in the More sheet and the
 * footer rather than at the top of a page. Opened in a new context so the
 * installed app never navigates itself to a page of raw XML.
 */
function feedLinks(): Array<{ href: string; label: string }> {
  return [
    { href: '/api/btc15-feed', label: 'Predictor RSS feed' },
    {
      href: `/api/wallet-feed?address=${encodeURIComponent(getTrackedAddress().address)}`,
      label: 'Wallet RSS feed',
    },
  ];
}

const TAB_ITEMS = NAV_ITEMS.filter((item) => item.tab);
const MORE_ITEMS = NAV_ITEMS.filter((item) => !item.tab);
const NAV_SECTIONS = byGroup(NAV_ITEMS);
const MORE_SECTIONS = byGroup(MORE_ITEMS);

function linkClass(base: string, active: string, alsoActive = false) {
  return ({ isActive }: { isActive: boolean }) =>
    isActive || alsoActive ? `${base} ${active}` : base;
}

/** Whether `pathname` is one of the pages `item` covers (see NavItem.covers). */
function coversPath(item: NavItem, pathname: string): boolean {
  return item.covers?.some((to) => pathname.startsWith(to)) ?? false;
}

/** Each page's name in the browser tab, history menu and app switcher. */
const PAGE_TITLES: Record<string, string> = {
  ...Object.fromEntries(NAV_ITEMS.map((item) => [item.to, item.label])),
  ...Object.fromEntries(PAPER_TEST_PAGES.map((page) => [page.to, page.label])),
  '/history': 'Transaction history',
  '/terms': 'Terms of Use',
  '/privacy': 'Privacy Policy',
  '/support': 'Support',
};
const HOME_TITLE = 'WhaleTail — Bitcoin Wallet Intelligence';

function documentTitle(pathname: string): string {
  if (pathname === '/') return HOME_TITLE;
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  return `${PAGE_TITLES[path] ?? 'Page not found'} · WhaleTail`;
}

// Phones scroll the page region (see .page in Layout.module.css); desktop
// scrolls the document. Setting both covers either layout.
function scrollPageTo(page: HTMLElement | null, top: number, behavior: ScrollBehavior) {
  const reduceMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const options: ScrollToOptions = { top, behavior: reduceMotion ? 'auto' : behavior };
  for (const el of [page, document.scrollingElement]) {
    if (!el) continue;
    if (typeof el.scrollTo === 'function') el.scrollTo(options);
    else el.scrollTop = top;
  }
}

function scrollToTop(page: HTMLElement | null, behavior: ScrollBehavior) {
  scrollPageTo(page, 0, behavior);
}

/** The current scroll offset, whichever element is scrolling. */
function currentScrollTop(page: HTMLElement | null): number {
  return Math.max(page?.scrollTop ?? 0, document.scrollingElement?.scrollTop ?? 0);
}

/**
 * Going back to a page puts it where the reader left it. Its data is
 * usually cached, but a part that loads lazily can make it briefly too short
 * to reach that offset, so this keeps trying for a moment.
 */
function restoreScroll(page: HTMLElement | null, top: number): () => void {
  let frame = 0;
  let tries = 0;
  const attempt = () => {
    scrollPageTo(page, top, 'auto');
    if (currentScrollTop(page) < top - 1 && tries++ < 60) {
      frame = window.requestAnimationFrame(attempt);
    }
  };
  attempt();
  return () => window.cancelAnimationFrame(frame);
}

export function Layout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const { pathname } = location;
  const navigationType = useNavigationType();
  // Where the reader had scrolled each history entry to, for going back.
  const scrollPositions = useRef(new Map<string, number>());
  const locationKeyRef = useRef(location.key);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const sheetRef = useRef<HTMLElement>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const onMorePage = MORE_ITEMS.some(
    (item) => pathname.startsWith(item.to) || coversPath(item, pathname),
  );

  // The app restores scroll itself (below), so the browser shouldn't also try.
  useEffect(() => {
    if ('scrollRestoration' in window.history)
      window.history.scrollRestoration = 'manual';
  }, []);

  // Remember the offset of the page on screen as the reader scrolls it.
  useEffect(() => {
    const page = pageRef.current;
    const record = () =>
      scrollPositions.current.set(locationKeyRef.current, currentScrollTop(page));
    page?.addEventListener('scroll', record, { passive: true });
    window.addEventListener('scroll', record, { passive: true });
    return () => {
      page?.removeEventListener('scroll', record);
      window.removeEventListener('scroll', record);
    };
  }, []);

  // Before paint, so a scroll event from the new page (the browser clamping
  // the offset to a shorter page) is never filed under the old entry.
  useLayoutEffect(() => {
    locationKeyRef.current = location.key;
  }, [location.key]);

  useEffect(() => {
    document.title = documentTitle(pathname);
  }, [pathname]);

  // Any navigation closes the More sheet. A new page starts at its top; going
  // back (or forward) returns to where that page was left.
  useEffect(() => {
    setMenuOpen(false);
    const saved = scrollPositions.current.get(location.key);
    if (navigationType === 'POP' && saved) return restoreScroll(pageRef.current, saved);
    scrollToTop(pageRef.current, 'auto');
    // Only a change of page moves the scroll, not a change of its ?query
    // (the predictor's tabs, a page's filters).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  // Tapping the tab for the page you're already on scrolls it back to the
  // top, as native tab bars do (and as the status bar can't here, since the
  // document itself doesn't scroll on phones).
  const chooseTab = (to: string) => {
    if (!menuOpen && pathname === to) scrollToTop(pageRef.current, 'smooth');
    setMenuOpen(false);
  };

  // Choosing a page from the sheet unmounts it, which would drop focus to
  // <body>; hand focus to the page's main region instead.
  const chooseFromSheet = () => {
    setMenuOpen(false);
    window.setTimeout(() => document.getElementById('main-content')?.focus(), 0);
  };

  // The sheet has no visible way to close once the viewport widens past the
  // breakpoint that hides the tab bar, so it would otherwise leave body
  // scroll permanently locked.
  useEffect(() => {
    if (!menuOpen) return;
    if (typeof window.matchMedia !== 'function') return;
    const desktop = window.matchMedia(DESKTOP_NAV_QUERY);
    const closeIfDesktop = () => {
      if (desktop.matches) setMenuOpen(false);
    };
    desktop.addEventListener('change', closeIfDesktop);
    return () => desktop.removeEventListener('change', closeIfDesktop);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        toggleRef.current?.focus();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    // The sheet is modal: the page behind it is inert (unfocusable and
    // hidden from assistive tech) until it closes.
    const page = pageRef.current;
    if (page) page.inert = true;
    // Move focus into the sheet so keyboard and screen-reader users land on
    // its links; Escape and the backdrop hand it back to the toggle.
    sheetRef.current?.querySelector<HTMLElement>('a')?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      if (page) page.inert = false;
    };
  }, [menuOpen]);

  // Phone and tablet navigation: a bottom tab bar, rendered outside the
  // sticky header (iOS Safari mis-hit-tests a fixed element inside a sticky
  // one), padded by the bottom safe area so it clears the home indicator and
  // the browser's floating toolbar.
  const tabBar = (
    <nav
      className={[styles.tabBar, menuOpen ? styles.tabBarMenuOpen : '']
        .filter(Boolean)
        .join(' ')}
      aria-label="Primary"
    >
      {TAB_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          onClick={() => chooseTab(item.to)}
          className={linkClass(styles.tab, styles.tabActive, coversPath(item, pathname))}
        >
          <span className={styles.tabIconWrap}>
            <item.Icon className={styles.navIcon} />
          </span>
          <span className={styles.tabLabel}>{item.short}</span>
        </NavLink>
      ))}
      <button
        ref={toggleRef}
        type="button"
        className={[
          styles.tab,
          styles.moreTab,
          onMorePage || menuOpen ? styles.tabActive : '',
        ]
          .filter(Boolean)
          .join(' ')}
        aria-expanded={menuOpen}
        aria-controls={menuOpen ? 'more-sheet' : undefined}
        onClick={() => setMenuOpen((open) => !open)}
      >
        <span className={styles.tabIconWrap}>
          <MoreIcon open={menuOpen} />
        </span>
        <span className={styles.tabLabel}>More</span>
      </button>
    </nav>
  );
  const sheet = menuOpen ? (
    <nav id="more-sheet" ref={sheetRef} className={styles.sheet} aria-label="More pages">
      {MORE_SECTIONS.map((section) => (
        <div key={section.group} className={styles.sheetSection}>
          <h2 className={styles.sheetHeading}>{NAV_GROUPS[section.group]}</h2>
          {section.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={chooseFromSheet}
              className={linkClass(
                styles.sheetLink,
                styles.sheetLinkActive,
                coversPath(item, pathname),
              )}
            >
              <item.Icon className={styles.navIcon} />
              {item.label}
            </NavLink>
          ))}
        </div>
      ))}
      <h2 className={styles.sheetHeading}>Feeds</h2>
      {feedLinks().map((feed) => (
        <a
          key={feed.href}
          href={feed.href}
          type="application/rss+xml"
          target="_blank"
          rel="noopener"
          onClick={() => setMenuOpen(false)}
          className={styles.sheetLink}
        >
          <RssIcon width={22} height={22} className={styles.navIcon} />
          {feed.label}
        </a>
      ))}
    </nav>
  ) : null;

  return (
    <div className={styles.shell}>
      <a href="#main-content" className="skip-link">
        Skip to main content
      </a>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <NavLink to="/" className={styles.brand} aria-label="WhaleTail home">
            <img
              src="/brand/whaletail-icon@2x.png"
              alt=""
              width={192}
              height={192}
              className={styles.brandMark}
            />
            <span>WhaleTail</span>
          </NavLink>
          <nav id="primary-nav" className={styles.nav} aria-label="Primary">
            {NAV_SECTIONS.map((section, i) => (
              <Fragment key={section.group}>
                {i > 0 && <span className={styles.navDivider} aria-hidden="true" />}
                {section.items.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    className={linkClass(
                      styles.navLink,
                      styles.navLinkActive,
                      coversPath(item, pathname),
                    )}
                  >
                    {item.label}
                  </NavLink>
                ))}
              </Fragment>
            ))}
          </nav>
        </div>
        <p className={styles.adviceBanner} role="note">
          <strong>Not financial or betting advice.</strong>{' '}
          <span className={styles.adviceBannerMore}>
            Informational research only — nothing here is a recommendation.{' '}
          </span>
          <Link to="/terms">Terms</Link>
        </p>
      </header>
      <div ref={pageRef} className={styles.page}>
        <main id="main-content" className={styles.main} tabIndex={-1}>
          <Outlet />
        </main>
        <footer className={styles.footer}>
          <p className={styles.footerDisclaimer}>
            <strong>Not financial, investment, or betting advice.</strong> WhaleTail is a
            read-only, informational research tool. Nothing on this site is a
            recommendation to buy, sell, or hold any asset, to trade any prediction-market
            contract, or to place any bet, and it never requests private keys, seed
            phrases, wallet signatures, or exchange credentials. See the{' '}
            <Link to="/methodology">methodology</Link> and{' '}
            <Link to="/terms">Terms of Use</Link> for full disclaimers.
          </p>
          <div className={styles.footerBottom}>
            <span>
              © {new Date().getFullYear()}{' '}
              <a href="https://reintslabs.com" target="_blank" rel="noreferrer noopener">
                Reints Labs LLC
              </a>
              . All rights reserved.
            </span>
            <nav className={styles.footerLinks} aria-label="Legal and support">
              <Link to="/terms">Terms of Use</Link>
              <Link to="/privacy">Privacy Policy</Link>
              <Link to="/support">Support</Link>
            </nav>
            <nav className={styles.footerLinks} aria-label="RSS feeds">
              {feedLinks().map((feed) => (
                <a
                  key={feed.href}
                  href={feed.href}
                  type="application/rss+xml"
                  target="_blank"
                  rel="noopener"
                >
                  {feed.label}
                </a>
              ))}
            </nav>
          </div>
        </footer>
      </div>

      {menuOpen && (
        <div
          className={styles.backdrop}
          aria-hidden="true"
          onClick={() => {
            setMenuOpen(false);
            toggleRef.current?.focus();
          }}
        />
      )}
      {sheet}
      {tabBar}
    </div>
  );
}
